import { useEffect, useState } from 'react';
import { FileText, Link2, X } from 'lucide-react';
import { fetchChatList, type ChatThreadSummary } from '../api/chatHistory';
import { Button } from '../components/atoms/Button';
import { cn } from '../lib/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called when the user picks a draft to link this document to. */
  onPick: (thread: ChatThreadSummary) => void;
}

/**
 * Full-pane picker listing the user's existing `chat_type:'draft'` chats
 * (web- AND plugin-originated — unfiltered). Selecting one links the current
 * Word document to that draft so subsequent edits flow into its chat thread
 * and show up in the web's draft sidebar.
 */
export function LinkDraftPicker({ open, onClose, onPick }: Props): JSX.Element | null {
  const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Unfiltered — include web-originated drafts so the user can link to any.
    fetchChatList('draft', { limit: 50 })
      .then((list) => {
        if (!cancelled) setThreads(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load drafts');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const filtered = query
    ? threads.filter((t) => (t.name ?? '').toLowerCase().includes(query.toLowerCase()))
    : threads;

  return (
    <div className="absolute inset-0 bg-background z-20 flex flex-col">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <Link2 className="w-4 h-4 text-primary flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold">Link to a draft</div>
            <div className="text-[11px] text-muted-foreground truncate">
              Continue edits on an existing draft instead of a standalone chat.
            </div>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} title="Cancel">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="px-3 py-2 border-b border-border">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search drafts…"
          className="w-full text-sm bg-background border border-border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {loading && <div className="p-4 text-xs text-muted-foreground">Loading…</div>}
        {error && <div className="p-4 text-xs text-red-600">{error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center text-center px-6 py-10 text-muted-foreground">
            <FileText className="w-5 h-5 mb-2" />
            <p className="text-xs">
              No drafts yet. Create one in Draft mode, or in the web app.
            </p>
          </div>
        )}
        {!loading && !error &&
          filtered.map((t) => (
            <button
              key={t.chatId}
              type="button"
              onClick={() => onPick(t)}
              className={cn(
                'w-full text-left px-3 py-2.5 border-b border-border hover:bg-accent transition-colors',
              )}
            >
              <div className="text-xs font-medium truncate">{t.name || 'Untitled'}</div>
              <div className="text-[11px] text-muted-foreground">
                {new Date(t.updatedAt ?? t.createdAt).toLocaleString()}
              </div>
            </button>
          ))}
      </div>
    </div>
  );
}
